import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput from '../../components/SliderInput';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const CalorieCalculator = () => {
  const [age, setAge] = useState(28);
  const [gender, setGender] = useState('male');
  const [height, setHeight] = useState(170);
  const [weight, setWeight] = useState(70);
  const [activity, setActivity] = useState(1.55);
  const [goal, setGoal] = useState('maintain');

  const calc = useMemo(() => {
    const bmr = gender === 'male'
      ? 10 * weight + 6.25 * height - 5 * age + 5
      : 10 * weight + 6.25 * height - 5 * age - 161;
    const tdee = bmr * activity;
    const targets = {
      lose: tdee - 500,
      mild: tdee - 250,
      maintain: tdee,
      mildGain: tdee + 250,
      gain: tdee + 500,
    };
    return { bmr, tdee, targets };
  }, [age, gender, height, weight, activity]);

  return (
    <CalculatorLayout
      title="Calorie Calculator"
      subtitle="Find your daily calorie needs based on activity and weight goal."
      category="Fitness"
      description={<>
        <p>This calculator estimates your Total Daily Energy Expenditure (TDEE) and recommends a calorie target for weight loss, maintenance, or gain. A 500-calorie daily deficit typically results in losing about 0.5 kg per week.</p>
        <p>Always consult a registered dietitian for personalized nutrition advice, especially if you have health conditions.</p>
      </>}
      formulaNote="TDEE = BMR × Activity Factor   •  1 kg fat ≈ 7700 calories"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Gender</label>
            <RadioGroup value={gender} onValueChange={setGender} className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="male" /> Male</label>
              <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="female" /> Female</label>
            </RadioGroup>
          </div>
          <SliderInput label="Age" unit="yr" value={age} onChange={setAge} min={15} max={100} step={1} />
          <SliderInput label="Height" unit="cm" value={height} onChange={setHeight} min={120} max={220} step={1} />
          <SliderInput label="Weight" unit="kg" value={weight} onChange={setWeight} min={30} max={200} step={0.5} />
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Activity Level</label>
            <Tabs value={String(activity)} onValueChange={(v) => setActivity(parseFloat(v))}>
              <TabsList className="flex flex-wrap h-auto">
                <TabsTrigger value="1.2" className="text-xs">Sedentary</TabsTrigger>
                <TabsTrigger value="1.375" className="text-xs">Light</TabsTrigger>
                <TabsTrigger value="1.55" className="text-xs">Moderate</TabsTrigger>
                <TabsTrigger value="1.725" className="text-xs">Very Active</TabsTrigger>
                <TabsTrigger value="1.9" className="text-xs">Athlete</TabsTrigger>
              </TabsList>
              <TabsContent value="1.2" /><TabsContent value="1.375" /><TabsContent value="1.55" /><TabsContent value="1.725" /><TabsContent value="1.9" />
            </Tabs>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Goal</label>
            <RadioGroup value={goal} onValueChange={setGoal} className="grid grid-cols-3 gap-2">
              {[
                { v: 'lose', l: 'Lose Weight' },
                { v: 'maintain', l: 'Maintain' },
                { v: 'gain', l: 'Gain Muscle' },
              ].map(o => (
                <label key={o.v} className={`text-center text-sm py-2 rounded-md border cursor-pointer ${goal === o.v ? 'bg-saffron-500 text-white border-saffron-500' : 'bg-white border-slate-200'}`}>
                  <RadioGroupItem value={o.v} className="sr-only" />{o.l}
                </label>
              ))}
            </RadioGroup>
          </div>
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Daily Calorie Target</div>
          <div className="font-display text-4xl font-bold text-bharat-navy display-font mt-1">
            {Math.round(calc.targets[goal === 'lose' ? 'lose' : goal === 'gain' ? 'gain' : 'maintain']).toLocaleString()} <span className="text-base text-slate-500">cal</span>
          </div>
          <div className="mt-5 space-y-2">
            <Row label="BMR (at rest)" value={Math.round(calc.bmr)} />
            <Row label="TDEE (with activity)" value={Math.round(calc.tdee)} tone="saffron" />
            <hr className="border-slate-200" />
            <Row label="Lose 0.5 kg / week" value={Math.round(calc.targets.lose)} tone="green" />
            <Row label="Mild loss" value={Math.round(calc.targets.mild)} />
            <Row label="Maintain" value={Math.round(calc.targets.maintain)} />
            <Row label="Mild gain" value={Math.round(calc.targets.mildGain)} />
            <Row label="Gain 0.5 kg / week" value={Math.round(calc.targets.gain)} tone="saffron" />
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Row = ({ label, value, tone }) => {
  const c = tone === 'saffron' ? 'text-saffron-700' : tone === 'green' ? 'text-indgreen-700' : 'text-slate-700';
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`display-font font-semibold ${c}`}>{value.toLocaleString()} cal</span>
    </div>
  );
};

export default CalorieCalculator;
